import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Doc, SharedDriveModel } from '../../../../../../../types';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getPageOfSharedDriveFiles,
  getPageOfSharedDriveFolders,
  getUserPermission,
  updateStarredDocStatus,
  UserKeys,
} from '../../../../../../api/users-api';
import { useDriveLabelBadges } from '../../../../../../shared/hooks/use-drive-label-badges';
import ScopesAlert from '../../../../../../shared/scopes-alert';
import { handleErrors } from '../../../../../../utils/errors';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import { useStore } from '../../../../../state/zustand-store';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import SharedDrivesCreateFolderDialog from './shared-drives-create-folder-dialog';
import SharedDrivesChildGridToolbar from './shared-drives-child-grid-toolbar';
import SharedDrivesRenameDialog from './shared-drives-rename-dialog';
import SharedDrivesChildMoveDialog from './shared-drives-child-move-dialog';
import SharedDrivesChildTrashDialog from './shared-drives-child-trash-dialog';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import SharedDrivesPermissionCheckDialog from '../shared-drives-parent/shared-drives-permission-check-dialog';
import SharedErrorBoundary from '../../../../../../shared/shared-error-boundary';
import DriveFileEditPermissionsDialog from '../../../../../drive/drive-file-edit-permissions-dialog';
import { createGridOverlays } from '../../../../../grid-overlay/no-data-overlay-helpers';
import UserAvatar from '../../../../../../shared/user-avatar';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import SharedDataGridClient from '../../../../../../shared/shared-data-grid-client';
import DriveDocLabelsCell from '../../../../../../shared/drive-doc-labels-cell';

import DriveAddShareDialog from '../../drive-add-share-dialog';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import Folder from '@mui/icons-material/Folder';
import Pageview from '@mui/icons-material/Pageview';
import Share from '@mui/icons-material/Share';
import Star from '@mui/icons-material/Star';
import StarOutline from '@mui/icons-material/StarOutline';
export interface PaginationModel {
  column: string;
  sortDirection: 'ASC' | 'DESC';
}
interface SharedDrivesChildGridProps {
  userEmail: string;
  primaryDomain: string;
  driveRoles: string[];
  changeLogRoles: string[];
  isLoading: boolean;
  selectedFolder: string;
  setSelectedFolder: Dispatch<SetStateAction<string>>;
  sharedDrives: SharedDriveModel[];
  paginationModel: PaginationModel;
  setPaginationModel: Dispatch<SetStateAction<PaginationModel>>;
}

export default function SharedDrivesChildGrid({
  userEmail,
  primaryDomain,
  isLoading,
  driveRoles,
  changeLogRoles,
  selectedFolder,
  setSelectedFolder,
  sharedDrives,
  paginationModel,
  setPaginationModel,
}: SharedDrivesChildGridProps) {
  const { setSnackbar } = useStore();
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [clickedDoc, setClickedDoc] = useState<Doc | null>(null);
  const [starDocSelection, setStarDocSelection] = useState<Partial<Doc>>();

  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState(false);
  const [openTrashDialog, setOpenTrashDialog] = useState(false);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [openMoveDialog, setOpenMoveDialog] = useState(false);
  const [openEditShareDialog, setOpenEditShareDialog] = useState(false);
  const [openAddShareDialog, setOpenAddShareDialog] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [openPermissionCheckDialog, setOpenPermissionCheckDialog] =
    useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();

  const url = window.location.href.toLowerCase();
  const isSharedDrives = url.includes('shared');
  const isDriveExplorer = url.includes('explorer');
  const { adminEmail } = useUserSettings();

  const [attemptedAction, setAttemptedAction] = useState<
    | 'createFolder'
    | 'moveDocument'
    | 'trashDocument'
    | 'renameDocument'
    | 'editShare'
  >('createFolder');

  const isASharedDrive = sharedDrives.find(
    (drive) => drive.id === selectedFolder
  );

  // fetch folders
  const {
    data: infiniteFolderData,
    isLoading: isLoadingFolders,
    isFetching: isFetchingFolders,
    fetchNextPage: fetchNextPageOfFolders,
    isFetchingNextPage: isFetchingNextPageOfFolders,
    hasNextPage: hasNextPageOfFolders,
  } = useInfiniteQuery({
    queryKey: [
      UserKeys.SharedDriveFolders,
      userEmail,
      selectedFolder,
      paginationModel.column,
      paginationModel.sortDirection,
    ],
    queryFn: ({ pageParam }) => {
      return getPageOfSharedDriveFolders(
        {
          cursor: pageParam,
          results: [],
          nameSpace: '',
          firstPull: true,
          searchTime: 0,
          pageSize: 100,
        },
        selectedFolder,
        paginationModel.column,
        paginationModel.sortDirection
      );
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.cursor,
    enabled: selectedFolder !== '',
    meta: {
      errorMessage:
        'There was an issue loading shared drive folders. Please refresh and try again.',
    },
  });

  const isFinishedGettingAllFolders =
    !!infiniteFolderData && !hasNextPageOfFolders;

  // fetch docs
  const {
    data: infiniteDocData,
    isLoading: docLoading,
    fetchNextPage: fetchNextPageOfDocs,
    isFetchingNextPage: isFetchingNextPageOfDocs,
    hasNextPage: hasNextPageOfDocs,
  } = useInfiniteQuery({
    queryKey: [
      UserKeys.SharedDriveDocuments,
      userEmail,
      selectedFolder,
      paginationModel.column,
      paginationModel.sortDirection,
    ],
    queryFn: ({ pageParam }) => {
      return getPageOfSharedDriveFiles(
        {
          cursor: pageParam,
          results: [],
          nameSpace: '',
          firstPull: true,
          searchTime: 0,
          pageSize: 100,
        },
        selectedFolder,
        paginationModel.column,
        paginationModel.sortDirection
      );
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.cursor,
    enabled: selectedFolder !== '' && isFinishedGettingAllFolders,
    meta: {
      errorMessage:
        'There was an issue loading document data. Please refresh and try again.',
    },
  });

  const { mutate, isPending: isUpdatingStarredStatus } = useMutation({
    mutationFn: (data: Partial<Doc>) =>
      updateStarredDocStatus(
        userEmail,
        data.id || '',
        !data.starred || false,
        changeLogMessage
      ),
    onSuccess: (_data, selectedDoc) => {
      const isFolder = selectedDoc.mimeType?.includes('folder');

      setSnackbar({
        open: true,
        severity: 'success',
        message: isFolder
          ? 'Folder updated successfully!'
          : 'Document updated successfully!',
      });

      setChangeLogMessage('');

      //   update user grid
      if (isFolder) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDriveFolders, userEmail, selectedFolder],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDriveDocuments, userEmail, selectedFolder],
        });
      }

      //   update starred folder
      if (isFolder) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveFolders, userEmail],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments, userEmail],
        });
      }
    },
    onError: (error, selectedDoc) => {
      handleErrors({
        error,
        snackbarMessage: `There was an issue updating this ${
          selectedDoc.mimeType?.includes('folder') ? 'folder' : 'document'
        }. Please refresh and try again.`,
      });
    },
    onSettled: () => {
      if (isSharedDrives) {
        createEvent(analytics.drive.shared_drives.content.star_drive_content, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.sharedDrives,
        });
      } else if (isDriveExplorer) {
        createEvent(
          analytics.drive.drive_explorer.shared_drives.content
            .star_drive_content,
          {
            module: ModuleList.modules.drive.name,
            section: ModuleList.modules.drive.sections.driveExplorer,
          }
        );
      } else {
        createEvent(
          analytics.user_management.user_details.drive.shared_drives.content
            .star_drive_content,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }

      setOpenChangeLogDialog(false);
    },
  });

  const finalRows = infiniteFolderData?.pages
    ?.flatMap((p) => p.results)
    .concat(infiniteDocData?.pages?.flatMap((p) => p.results) || []) as Doc[];
  const selectedSharedDocs = finalRows?.filter((doc) =>
    selectedDocs.includes(doc.id)
  );

  const {
    driveLabelsFeatureEnabled,
    canViewDriveLabels,
    driveLabelsAccessGranted,
    checkingDriveLabelsAccess,
    getLabelBadgeInfo,
  } = useDriveLabelBadges(driveRoles);

  const columns: GridColDef<Doc>[] = [
    {
      field: 'title',
      headerName: 'Name',
      editable: false,
      sortable: true,
      filterable: false,
      flex: 1,
      display: 'flex',
      renderCell: (params) => {
        const avatar = (
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <img
              src={params.row.iconUrl}
              style={{ width: 15, height: 15, borderRadius: 2 }}
            />
            <Typography
              variant="body2"
              sx={{
                maxWidth: 240,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {params.row.title}
            </Typography>
          </Stack>
        );
        return avatar;
      },
    },
    {
      field: 'owner',
      headerName: 'Owner',
      editable: false,
      sortable: false,
      filterable: false,
      minWidth: 150,
      display: 'flex',
      renderCell: (params) => {
        const ownerEmail = params.row.ownerEmail
          ? params.row.ownerEmail
          : params.row.owners.length > 0
          ? params.row.owners[0].emailAddress
          : '';

        const avatar = (
          <Stack flexDirection="row" alignItems="center" gap={1}>
            {ownerEmail !== '' ? (
              <UserAvatar
                userEmail={ownerEmail}
                sx={{ width: 24, height: 24 }}
              />
            ) : null}

            <Typography
              variant="body2"
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {ownerEmail !== '' ? params.row.owner : '-'}
            </Typography>
          </Stack>
        );
        return avatar;
      },
    },
    {
      field: 'modifiedDate',
      headerName: 'Last Modified',
      editable: false,
      sortable: false,
      filterable: false,
      minWidth: 115,
      type: 'dateTime',
      valueGetter: (_value, row) => {
        if (row.modifiedDate === 0) return '';
        return new Date(row.modifiedDate);
      },
      valueFormatter: (value) => {
        if (value === '') return '';
        return new Date(value as string).toLocaleDateString();
      },
    },
    {
      field: 'fileSize',
      headerName: 'File Size',
      editable: false,
      sortable: false,
      filterable: false,
      type: 'number',
      display: 'flex',
      valueGetter: (_value, row) => {
        if (row.mimeType.includes('folder')) {
          return -1;
        } else {
          return row.fileSize;
        }
      },
      valueFormatter: (value) => {
        if (value === -1) return '-';
        if (value === 0) return '0 B';
        if (value < 1000) return value + ' B';
        if (value < 1000000) return Math.round(value / 1000) + ' KB';
        if (value < 1000000000) return Math.round(value / 1000000) + ' MB';
      },
    },
    ...(driveLabelsFeatureEnabled &&
    canViewDriveLabels &&
    driveLabelsAccessGranted
      ? [
          {
            field: 'labelTitles',
            headerName: 'Labels',
            display: 'flex',
            minWidth: 150,
            flex: 0.5,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <DriveDocLabelsCell
                doc={params.row}
                getLabelBadgeInfo={getLabelBadgeInfo}
              />
            ),
          } as GridColDef<Doc>,
        ]
      : []),
    {
      field: 'actions',
      type: 'actions',
      minWidth: 120,
      headerName: 'Actions',
      getActions: (params: GridRowParams) => {
        const buttonArray = [];

        if (isAbleToSeeUIElement(driveRoles, DriveRoles.CanStarDocs.value)) {
          buttonArray.push(
            <GridActionsCellItem
              label={
                params.row.starred ? 'Remove from Starred' : 'Add to Starred'
              }
              icon={
                isUpdatingStarredStatus && clickedDoc?.id === params.row.id ? (
                  <CircularProgress size={20} />
                ) : (
                  <Tooltip
                    arrow
                    title={
                      params.row.starred
                        ? 'Remove from Starred'
                        : 'Add to Starred'
                    }
                  >
                    {params.row.starred ? <Star /> : <StarOutline />}
                  </Tooltip>
                )
              }
              onClick={() => {
                if (
                  isAbleToSeeUIElement(
                    changeLogRoles,
                    DriveRoles.CanStarDocs.value
                  )
                ) {
                  setStarDocSelection(params.row);
                  setOpenChangeLogDialog(true);
                } else {
                  mutate(params.row);
                  setClickedDoc(params.row);
                }
              }}
            />
          );
        }

        if (
          isAbleToSeeUIElement(driveRoles, DriveRoles.CanViewDocuments.value)
        ) {
          buttonArray.push(
            <GridActionsCellItem
              label={'View'}
              icon={<Pageview />}
              onClick={() => {
                let url = '';

                if (params.row.mimeType.includes('folder')) {
                  url = `https://drive.google.com/drive/folders/${params.row.id}`;
                } else {
                  url = `https://drive.google.com/a/${primaryDomain}/file/d/${params.row.id}/view`;
                }

                window.open(url, '_blank', 'noopener, noreferrer');
              }}
              showInMenu
            />
          );
        }

        if (isAbleToSeeUIElement(driveRoles, DriveRoles.CanRenameDocs.value)) {
          buttonArray.push(
            <GridActionsCellItem
              label={'Rename'}
              icon={<DriveFileRenameOutline />}
              onClick={async () => {
                const hasPermission = await queryClient.fetchQuery({
                  queryKey: [
                    UserKeys.SharedDriveUserPermission,
                    userEmail,
                    selectedFolder,
                  ],
                  queryFn: () =>
                    getUserPermission(
                      selectedFolder,
                      userEmail,
                      !!isASharedDrive
                    ),
                  retry: false,
                  staleTime: 1000 * 60 * 2,
                });

                setClickedDoc(params.row);

                const allowedPermissions = [
                  'writer',
                  'fileOrganizer',
                  'organizer',
                ];

                const hasCorrectPermission =
                  hasPermission && allowedPermissions.includes(hasPermission);

                if (hasCorrectPermission) {
                  setOpenRenameDialog(true);
                } else if (!hasCorrectPermission) {
                  setOpenPermissionCheckDialog(true);
                  setAttemptedAction('renameDocument');
                }
              }}
              showInMenu
            />
          );
        }

        if (isAbleToSeeUIElement(driveRoles, DriveRoles.CanShareDocs.value)) {
          buttonArray.push(
            <GridActionsCellItem
              label={'Share'}
              icon={<Share />}
              onClick={() => {
                setClickedDoc(params.row);
                setOpenEditShareDialog(true);
              }}
              showInMenu
            />
          );
        }

        return buttonArray;
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {driveLabelsFeatureEnabled &&
          canViewDriveLabels &&
          !driveLabelsAccessGranted &&
          !checkingDriveLabelsAccess && (
            <Box sx={{ flexShrink: 0 }}>
              <ScopesAlert apiKey="driveLabels" />
            </Box>
          )}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <SharedDataGridClient
            apiRef={apiRef}
            checkboxSelection
            disableColumnMenu
            keepNonExistentRowsSelected={false}
            rows={finalRows || []}
            columns={columns}
            slots={{
              toolbar: SharedDrivesChildGridToolbar as never,
              ...createGridOverlays(
                'Documents/Folders',
                <Folder />,
                'This folder is empty'
              ),
            }}
            slotProps={{
              toolbar: {
                selectedFolder,
                selectedDocs,
                setSelectedFolder,
                setOpenCreateFolderDialog,
                setOpenMoveDialog,
                setOpenAddShareDialog,
                setOpenTrashDialog,
                driveRoles,
                sharedDrives,
                userEmail,
                setOpenPermissionCheckDialog,
                setAttemptedAction,
              } as never,
            }}
            onRowClick={(params) => {
              if (params.row.mimeType.includes('folder')) {
                setSelectedFolder(params.row.id);
              }
            }}
            selectedRows={selectedDocs}
            setSelectedRows={setSelectedDocs}
            loading={
              isLoading ||
              docLoading ||
              isLoadingFolders ||
              isFetchingFolders ||
              isFetchingNextPageOfFolders ||
              isFetchingNextPageOfDocs
            }
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
            onRowsScrollEnd={() => {
              if (!isFetchingNextPageOfFolders && hasNextPageOfFolders) {
                void fetchNextPageOfFolders();
              } else {
                if (
                  !isFetchingNextPageOfDocs &&
                  hasNextPageOfDocs &&
                  isFinishedGettingAllFolders
                ) {
                  void fetchNextPageOfDocs();
                }
              }
            }}
            sortingMode="server"
            onSortModelChange={(newModel) => {
              if (
                isFetchingNextPageOfFolders ||
                isFetchingNextPageOfDocs ||
                isLoading
              )
                return;

              let columnName = 'name';
              if (newModel.length > 0 && newModel[0].field === 'modifiedDate') {
                columnName = 'modifiedTime';
              }

              setPaginationModel({
                column: columnName,
                sortDirection:
                  newModel.length > 0 && newModel[0].sort === 'asc'
                    ? 'ASC'
                    : newModel.length > 0 && newModel[0].sort === 'desc'
                    ? 'DESC'
                    : 'ASC',
              });
            }}
          />
        </Box>
      </Box>
      {openCreateFolderDialog && (
        <SharedErrorBoundary section="Shared Drives Child Create Folder Dialog">
          <SharedDrivesCreateFolderDialog
            open={openCreateFolderDialog}
            handleClose={() => setOpenCreateFolderDialog(false)}
            userEmail={userEmail}
            selectedFolder={selectedFolder}
            changeLogRoles={changeLogRoles}
          />
        </SharedErrorBoundary>
      )}
      {clickedDoc && openRenameDialog && (
        <SharedErrorBoundary section="Shared Drives Child Rename Dialog">
          <SharedDrivesRenameDialog
            open={openRenameDialog}
            handleClose={() => setOpenRenameDialog(false)}
            userEmail={userEmail}
            selectedFolder={selectedFolder}
            clickedDoc={clickedDoc}
            changeLogRoles={changeLogRoles}
          />
        </SharedErrorBoundary>
      )}
      {selectedDocs && openMoveDialog && (
        <SharedErrorBoundary section="Shared Drives Child Move Dialog">
          <SharedDrivesChildMoveDialog
            open={openMoveDialog}
            handleClose={() => setOpenMoveDialog(false)}
            selectedDocs={selectedSharedDocs}
            setSelectedDocs={setSelectedDocs}
            changeLogRoles={changeLogRoles}
            selectedFolder={selectedFolder}
          />
        </SharedErrorBoundary>
      )}
      {openEditShareDialog && clickedDoc && (
        <SharedErrorBoundary section="Shared Drives Child Permission Dialog">
          <DriveFileEditPermissionsDialog
            open={openEditShareDialog}
            handleClose={() => setOpenEditShareDialog(false)}
            primaryDomain={primaryDomain}
            selectedDoc={clickedDoc}
            setSelectedDoc={setClickedDoc}
            driveRoles={driveRoles}
            changeLogRoles={changeLogRoles}
            loggedInUser={userEmail}
          />
        </SharedErrorBoundary>
      )}
      {openAddShareDialog && selectedSharedDocs && (
        <SharedErrorBoundary section="Shared Drives Child Add Share Dialog">
          <DriveAddShareDialog
            open={openAddShareDialog}
            handleClose={() => setOpenAddShareDialog(false)}
            userEmail={adminEmail}
            selectedDocs={selectedSharedDocs}
            changeLogRoles={changeLogRoles}
            fromSharedDrives={true}
          />
        </SharedErrorBoundary>
      )}
      {openTrashDialog && selectedDocs && (
        <SharedErrorBoundary section="Shared Drives Child Trash Dialog">
          <SharedDrivesChildTrashDialog
            open={openTrashDialog}
            handleClose={() => setOpenTrashDialog(false)}
            userEmail={userEmail}
            selectedDocs={selectedSharedDocs}
            selectedFolder={selectedFolder}
            changeLogRoles={changeLogRoles}
          />
        </SharedErrorBoundary>
      )}
      {openChangeLogDialog && (
        <SharedErrorBoundary section="Shared Drives Child Change Log Dialog">
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              mutate(starDocSelection!);
            }}
            mutateLoading={isUpdatingStarredStatus}
          />
        </SharedErrorBoundary>
      )}
      {openPermissionCheckDialog ? (
        <SharedErrorBoundary section="Shared Drives Child Permission Check Dialog">
          <SharedDrivesPermissionCheckDialog
            open={openPermissionCheckDialog}
            handleClose={() => setOpenPermissionCheckDialog(false)}
            folderOrDriveId={selectedFolder}
            minimumRequiredRole={
              attemptedAction === 'createFolder' ||
              attemptedAction === 'renameDocument'
                ? 'writer'
                : attemptedAction === 'trashDocument'
                ? 'fileOrganizer'
                : 'organizer'
            }
            actionAfterPermissionGranted={() => {
              if (attemptedAction === 'createFolder') {
                setOpenCreateFolderDialog(true);
              } else if (attemptedAction === 'moveDocument') {
                setOpenMoveDialog(true);
              } else if (attemptedAction === 'trashDocument') {
                setOpenTrashDialog(true);
              } else if (attemptedAction === 'renameDocument') {
                setOpenRenameDialog(true);
              } else if (attemptedAction === 'editShare') {
                setOpenEditShareDialog(true);
              }
            }}
            nameOfSharedDrive={isASharedDrive ? isASharedDrive.name : null}
          />
        </SharedErrorBoundary>
      ) : null}
    </>
  );
}
